import { useState } from "react";
import { visit } from "@hotwired/turbo";
import { useApiRequest } from "../../hooks/useApiRequest";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { ChevronDown, Check } from "lucide-react";
import { AvatarAppearance, Avatar } from "../../types/avatar_appearances.d";
import DirectUploadInput from "../shared/DirectUploadInput";

interface AvatarAppearanceCreateProps {
  avatarAppearance: AvatarAppearance;
  avatars: Avatar[];
}

// #STYLE_GUIDE: Create component for new record creation
// - Uses daisyUI components for consistent styling
// - Navigation via Turbo.visit() for SPA-like experience
// - API calls via useApiRequest hook for consistency
// #TODO: This is a basic implementation - customize form fields and validation as needed
export default function AvatarAppearanceCreate({
  avatarAppearance,
  avatars,
}: AvatarAppearanceCreateProps) {
  const [formData, setFormData] = useState({
    avatarId: "",
    name: "",
    mediaFile: "",
  });

  const [selectedAvatar, setSelectedAvatar] = useState<Avatar | null>(null);
  const [avatarQuery, setAvatarQuery] = useState("");

  const { loading, makeRequest, getFieldError } = useApiRequest({
    onSuccess: (data) => {
      // #TURBO: Use Turbo.visit for navigation instead of window.location
      visit(`/avatar_appearances/${data.id}`);
    },
    onError: (error) => {
      console.error("Failed to create avatar_appearance:", error);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const submitData = {
      ...formData,
      avatarId: selectedAvatar?.id || "",
    };

    await makeRequest("POST", "/api/v1/avatar_appearances", {
      avatarAppearance: submitData,
    });
  };

  const handleCancel = () => {
    // #TURBO: Use Turbo.visit for navigation
    visit("/avatar_appearances");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Create New AvatarAppearance</h1>
        <button onClick={handleCancel} className="btn btn-outline">
          Cancel
        </button>
      </div>

      {/* Form */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Avatar field */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">Avatar</span>
              </label>
              <div className="relative">
                <Listbox
                  value={selectedAvatar}
                  onChange={(value) => {
                    setSelectedAvatar(value);
                    setFormData((prev) => ({
                      ...prev,
                      avatarId: value?.id || "",
                    }));
                  }}
                >
                  <ListboxButton
                    className={`input input-bordered w-full text-left ${
                      getFieldError("avatarId") ? "input-error" : ""
                    }`}
                  >
                    {selectedAvatar?.displayName || "Select avatar"}
                    <ChevronDown className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none w-5 h-5" />
                  </ListboxButton>
                  <ListboxOptions className="absolute z-10 mt-1 w-full bg-base-100 shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    {(() => {
                      const filteredAvatar = avatars.filter((item) =>
                        item.displayName
                          .toLowerCase()
                          .includes(avatarQuery.toLowerCase())
                      );

                      if (filteredAvatar.length === 0) {
                        return (
                          <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                            No avatar found.
                          </div>
                        );
                      }

                      return filteredAvatar.map((item) => (
                        <ListboxOption
                          key={item.id}
                          value={item}
                          className="relative cursor-default select-none py-2 pl-10 pr-4 hover:bg-base-200"
                        >
                          {item.displayName}
                          {selectedAvatar?.id === item.id && (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                              <Check className="h-5 w-5" aria-hidden="true" />
                            </span>
                          )}
                        </ListboxOption>
                      ));
                    })()}
                  </ListboxOptions>
                </Listbox>
              </div>
              {getFieldError("avatarId") && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {getFieldError("avatarId")}
                  </span>
                </label>
              )}
            </div>

            {/* Name field */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">Name</span>
              </label>
              <input
                type="text"
                className={`input input-bordered ${
                  getFieldError("name") ? "input-error" : ""
                }`}
                placeholder="Enter name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
              />
              {getFieldError("name") && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {getFieldError("name")}
                  </span>
                </label>
              )}
            </div>

            {/* Media File Upload */}
            <DirectUploadInput
              label="Media File (Image or Video)"
              accept="image/*,video/*"
              onUploadComplete={(signedBlobId) => {
                setFormData((prev) => ({
                  ...prev,
                  mediaFile: signedBlobId,
                }));
              }}
              error={getFieldError("mediaFile") || undefined}
            />

            {/* Submit buttons */}
            <div className="form-control mt-6">
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="btn btn-primary flex-1"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Creating...
                    </>
                  ) : (
                    "Create AvatarAppearance"
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn btn-outline"
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
